
import { useState, useCallback } from 'react';
import { alphaVantageService } from '@/services/alphaVantage/alphaVantageService';
import { yahooFinanceService } from '@/services/yahooFinance/yahooFinanceService';
import { COMMON_VALID_SYMBOLS, isDevEnvironment, normalizeSymbol } from './utils';
import { SymbolStatusMap, ValidatedSymbolsMap } from './types';

export const useSymbolValidation = () => {
  const [symbolStatus, setSymbolStatus] = useState<SymbolStatusMap>({});
  const [validatedSymbols, setValidatedSymbols] = useState<ValidatedSymbolsMap>({});
  const [apiCallCount, setApiCallCount] = useState(0);
  const [isDevMode] = useState(isDevEnvironment());

  // Reset API call count every minute
  useState(() => {
    const interval = setInterval(() => {
      setApiCallCount(0);
    }, 60000);
    
    return () => clearInterval(interval);
  });

  // Validate a symbol exists in the market
  const validateSymbol = useCallback(async (symbol: string): Promise<boolean> => {
    const symbolUpperCase = normalizeSymbol(symbol);
    setSymbolStatus(prev => ({ ...prev, [symbolUpperCase]: 'loading' }));
    
    // Check if we've already validated this symbol
    if (validatedSymbols[symbolUpperCase] !== undefined) {
      setSymbolStatus(prev => ({ ...prev, [symbolUpperCase]: validatedSymbols[symbolUpperCase] ? 'valid' : 'invalid' }));
      return validatedSymbols[symbolUpperCase];
    }
    
    // Check if it's in our common symbols list for quick validation
    if (COMMON_VALID_SYMBOLS.includes(symbolUpperCase)) {
      setSymbolStatus(prev => ({ ...prev, [symbolUpperCase]: 'valid' }));
      setValidatedSymbols(prev => ({ ...prev, [symbolUpperCase]: true }));
      return true;
    }
    
    // In development mode, be more permissive to avoid API rate limits
    if (isDevMode) {
      // Validate if symbol follows standard pattern (1-5 uppercase letters)
      const isValidFormat = /^[A-Z]{1,5}$/.test(symbolUpperCase);
      setSymbolStatus(prev => ({ ...prev, [symbolUpperCase]: isValidFormat ? 'valid' : 'invalid' }));
      setValidatedSymbols(prev => ({ ...prev, [symbolUpperCase]: isValidFormat }));
      return isValidFormat;
    }
    
    try {
      // First try Alpha Vantage if we haven't hit rate limits
      if (apiCallCount < 5) {
        setApiCallCount(prev => prev + 1);
        const isValidAlpha = await alphaVantageService.validateSymbol(symbolUpperCase);
        
        if (isValidAlpha) {
          setSymbolStatus(prev => ({ ...prev, [symbolUpperCase]: 'valid' }));
          setValidatedSymbols(prev => ({ ...prev, [symbolUpperCase]: true }));
          return true;
        }
      }
      
      // If Alpha Vantage fails or hits rate limits, try Yahoo Finance as fallback
      const isValidYahoo = await yahooFinanceService.validateSymbol(symbolUpperCase);
      
      setSymbolStatus(prev => ({ ...prev, [symbolUpperCase]: isValidYahoo ? 'valid' : 'invalid' }));
      setValidatedSymbols(prev => ({ ...prev, [symbolUpperCase]: isValidYahoo }));
      return isValidYahoo;
    } catch (err) {
      console.error(`Error validating symbol ${symbolUpperCase}:`, err);
      
      // In case of API error, fallback to format validation
      const isValidFormat = /^[A-Z]{1,5}$/.test(symbolUpperCase);
      setSymbolStatus(prev => ({ ...prev, [symbolUpperCase]: isValidFormat ? 'valid' : 'invalid' }));
      setValidatedSymbols(prev => ({ ...prev, [symbolUpperCase]: isValidFormat }));
      return isValidFormat;
    }
  }, [apiCallCount, isDevMode, validatedSymbols]);

  return {
    symbolStatus,
    validateSymbol,
    apiCallCount,
    setApiCallCount,
    isDevMode
  };
};
